// Phase 14e-b — operator-override + policy-override button + reason-required modal.
//
// Per the 14e-4 lock tier matrix: operator-override actions (record-payment,
// record-eft-dispatch, record-eft-rejection, cancel-by-operator) and
// policy-override actions (waive-interest, skip-next-dunning) use this
// component. Reason ≥10 chars required; optional `extraFields` slot
// renders above the reason for actions that take additional input
// (e.g. record-payment's amount + cheque number).
//
// Distinct from <DangerButton>: no typed-name match (these are reversible
// or appendable corrections), amber/secondary visual cue (not red).
//
// TOTP step-up: some hosts (cancel-by-operator, record-manual-payment,
// target-caregiver) call endpoints that DO carry TotpFreshGuard. The
// catch block detects TotpStepUpRequiredError and closes the modal
// silently — TotpStepUpModalProvider's global MutationCache hook opens
// the step-up modal + replays the mutation. See the catch in submit().
//
// Modal pattern mirrors 14e-a-round-2 + <DangerButton> (ARIA dialog +
// aria-modal + aria-labelledby + Escape close).

import { useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Field, useFocusTrap } from '@truecare/ui';
import { isStepUpError, messageOf } from '@truecare/api-client';

export interface OverrideActionButtonProps {
  /** Modal title — e.g. "Record cheque payment", "Waive interest". */
  actionLabel: string;
  /** Defaults to "Reason"; override for specific actions e.g. "Rejection reason". */
  reasonLabel?: string;
  minReasonLength?: number;
  /** Extra inputs rendered above the reason textarea. Used for
   *  record-payment (amount + reference), record-eft-dispatch (date),
   *  record-eft-rejection (operator notes), etc. Pass a ReactNode that
   *  manages its own state + reads/writes via callback prop. */
  extraFields?: ReactNode;
  /** Called when operator confirms. Reason is always passed; extras
   *  are the host's responsibility to capture via extraFields. */
  onConfirm: (input: { reason: string }) => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  /** Button label. */
  children: ReactNode;
  /** Optional inline instructions rendered as an info Alert. */
  instructions?: ReactNode;
  'data-testid'?: string;
}

export function OverrideActionButton({
  actionLabel,
  reasonLabel = 'Reason',
  minReasonLength = 10,
  extraFields,
  onConfirm,
  loading,
  disabled,
  children,
  instructions,
  'data-testid': testId,
}: OverrideActionButtonProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus in on open, trap Tab, Escape-to-close, restore focus to the
  // trigger on close (H-F4). Replaces the prior Escape-only handler.
  useFocusTrap(panelRef, { active: open, onEscape: () => closeModal() });

  function closeModal(): void {
    setOpen(false);
    setReason('');
    setError(null);
  }

  const reasonOk = reason.length >= minReasonLength;
  const canSubmit = reasonOk && !submitting;

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm({ reason });
      closeModal();
    } catch (e) {
      // Step-up errors are handled by TotpStepUpModalProvider's global
      // MutationCache hook; the queued mutation auto-retries after
      // verification. Close the modal silently so the operator doesn't
      // see a stale "TOTP step-up required" alert + double-submit on
      // an already-replayed mutation.
      if (isStepUpError(e)) {
        closeModal();
      } else {
        // Surfaces both pre-mutate validation throws AND mutation
        // errors (e.g. backend 422). Modal stays open so operator can
        // correct + retry.
        // Audit #10 — messageOf() reads the backend's real Convention-03 reason
        // from error.body.message; e.message alone is the generic "API 4xx".
        setError(messageOf(e, 'Submission failed. Try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
        loading={loading}
        disabled={disabled}
        data-testid={testId}
      >
        {children}
      </Button>

      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="override-action-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
          data-testid="override-action-modal"
        >
          <div className="w-full max-w-md rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
            <h2
              id="override-action-modal-title"
              className="font-heading text-xl text-forest"
            >
              {actionLabel}
            </h2>

            {instructions && (
              <Alert tone="info" className="mt-3">
                {instructions}
              </Alert>
            )}

            {error && (
              <Alert tone="danger" className="mt-3" data-testid="override-action-error">
                {error}
              </Alert>
            )}

            {extraFields && <div className="mt-4 flex flex-col gap-3">{extraFields}</div>}

            <Field
              label={`${reasonLabel} (≥${minReasonLength} characters)`}
              className="mt-4"
            >
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full rounded-md border border-border bg-cream p-2 text-sm"
                data-testid="override-action-reason"
              />
            </Field>

            <div className="mt-5 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                onClick={closeModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!canSubmit}
                loading={submitting}
                onClick={() => void submit()}
                data-testid="override-action-submit"
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
